import HttpService from '../Service/HttpService'
import { getClosestElement, getElementChild, getElementChildren, getElements } from '../Utility/Dom'

class SearchAutocomplete {
  private apiEndpointParameter: string = '?eID=keSearchPremiumAutoComplete&wordStartsWith='
  private inputFields: NodeListOf<HTMLElement>
  private searchInputFieldMinCharacterCount: number = 3
  private suggestItemCount: number

  constructor () {
    this.inputFields = getElements('search-input')
    this.suggestItemCount = -1
  }

  public init (): void {
    this.addEvents()
  }

  private addEvents (): void {
    this.inputFields.forEach((inputField: HTMLInputElement): void => {
      inputField.addEventListener('input', (e): void => this.fetchSuggestions(e))
    })

    document.addEventListener('click', (e): void => {
      const clickedElement = e.target as HTMLElement

      if (
        getClosestElement(clickedElement, 'search-autocomplete-box-item') === null &&
        getClosestElement(clickedElement, 'search-input') === null
      ) {
        this.hideAndClearAllAutocompleteBoxes()
        this.resetSearchInputFields()
      }
    })

    document.addEventListener('keydown', (e) => this.handleKeyDown(e))
  }

  private fetchSuggestions (event: Event): void {
    event.stopPropagation()
    const inputField = event.target as HTMLInputElement
    const inputFieldValue = inputField.value.trim()
    const currentCharCount = inputFieldValue.length
    const searchForm = inputField.closest('form')

    if (searchForm === null) {
      return
    }

    const autocompleteBox = getElementChild(searchForm, 'search-autocomplete-box') as HTMLElement
    const autocompleteItemTemplate = getElementChild(searchForm, 'search-autocomplete-box-item-template') as HTMLTemplateElement | null

    if (currentCharCount >= this.searchInputFieldMinCharacterCount) {
      const url = window.location.origin +
        this.apiEndpointParameter + encodeURI(inputFieldValue) +
        '&language=' + (searchForm.getAttribute('data-language') || 0)

      HttpService.get(url).then((suggestItems) => {
        if (!Array.isArray(suggestItems) || suggestItems.length === 0) {
          return
        }

        inputField.setAttribute('aria-expanded', 'true')
        autocompleteBox.innerHTML = ''

        suggestItems.forEach((suggestItem, index) => {
          let searchAutoCompleteItem = null as HTMLElement | null

          if (autocompleteItemTemplate) {
            searchAutoCompleteItem = autocompleteItemTemplate.content.cloneNode(true) as HTMLElement
          }

          if (searchAutoCompleteItem !== null) {
            const searchAutocompleteOption = getElementChild(searchAutoCompleteItem, 'search-autocomplete-box-item') as HTMLElement | null

            if (searchAutocompleteOption !== null) {
              searchAutocompleteOption.innerHTML = suggestItem
              searchAutocompleteOption.setAttribute('id', searchAutocompleteOption.getAttribute('id') + index)

              searchAutocompleteOption.addEventListener('click', () => {
                inputField.value = searchAutocompleteOption.innerText
                this.hideAndClearAutocompleteBox(inputField, autocompleteBox)
                this.resetSearchInputFields()
                searchForm.submit()
              })
            }

            autocompleteBox.append(searchAutoCompleteItem)
          }
        })
      })
      .catch((error): void => {
        if (error.name === 'AbortError') {
          return
        }

        console.error('Autocomplete fetch failed:', error)
      })
    }

    if (currentCharCount < this.searchInputFieldMinCharacterCount) {
      this.hideAndClearAutocompleteBox(inputField, autocompleteBox)
      this.resetSearchInputFields()
    }

    this.suggestItemCount = -1
  }

  private handleKeyDown (event): void {
    const element = event.target as HTMLInputElement
    const isElementSearchInput = element.matches('[data-js=search-input]')

    if (
      isElementSearchInput === true &&
      this.isInputFieldFocused(element)
    ) {
      const key = event.key
      const searchForm = element.closest('form')

      if (searchForm === null) {
        return
      }

      const autocompleteBox = getElementChild(searchForm, 'search-autocomplete-box') as HTMLElement
      let isEnterKeyPressed = false

      if (element.getAttribute('aria-expanded') === 'false') {
        return
      }

      const autocompleteItems = getElementChildren(autocompleteBox, 'search-autocomplete-box-item')
      const autocompleteItemCount = autocompleteItems.length

      switch (key) {
        case 'ArrowDown':
          event.preventDefault()

          if (autocompleteItemCount) {
            this.suggestItemCount++

            if (this.suggestItemCount >= autocompleteItemCount) {
              this.suggestItemCount = autocompleteItemCount - 1
            }
          }
          break

        case 'ArrowUp':
          event.preventDefault()

          if (autocompleteItemCount) {
            this.suggestItemCount--

            if (this.suggestItemCount < 0) {
              this.suggestItemCount = -1
            }
          }
          break

        case 'Enter':
          isEnterKeyPressed = true
          break

        case 'Escape':
          if (
            document.activeElement.matches('[data-js=search-input]') === true &&
            this.isAutocompleteBoxActive() === true
          ) {
            event.preventDefault()
            this.hideAndClearAllAutocompleteBoxes()
          }
      }

      if (this.suggestItemCount === -1) {
        element.removeAttribute('aria-activedescendant')
      }

      autocompleteItems.forEach((autocompleteItem: HTMLElement, i: number) => {
        autocompleteItem.setAttribute('aria-selected', 'false')

        if (i === this.suggestItemCount) {
          autocompleteItem.setAttribute('aria-selected', 'true')
          element.setAttribute('aria-activedescendant', autocompleteItem.getAttribute('id'))

          this.scrollToSelectedItem(autocompleteBox, autocompleteItem)
        }
      })

      if (isEnterKeyPressed === true) {
        const activeSuggestItem = autocompleteBox.querySelector('[aria-selected="true"]') as HTMLElement | null

        if (activeSuggestItem !== null) {
          event.preventDefault()
          element.value = activeSuggestItem.innerText
          this.hideAndClearAutocompleteBox(element, autocompleteBox)

          element.scrollIntoView({
            block: 'nearest',
            inline: 'nearest'
          })
        }
      }
    }
  }

  private hideAndClearAllAutocompleteBoxes (): void {
    const autocompleteBoxes = getElements('search-autocomplete-box')

    autocompleteBoxes.forEach((autocompleteBox: HTMLElement) => {
      const inputField = getElementChild(autocompleteBox.parentElement, 'search-input') as HTMLInputElement | null

      if (inputField !== null) {
        this.hideAndClearAutocompleteBox(inputField, autocompleteBox)
      }
    })
  }

  private hideAndClearAutocompleteBox (inputField: HTMLInputElement, autocompleteBox : HTMLElement) {
    inputField.setAttribute('aria-expanded', 'false')
    inputField.removeAttribute('aria-activedescendant')
    autocompleteBox.innerHTML = ''
  }

  private isAutocompleteBoxActive () {
    const inputFields = getElements('search-input')

    return Array.from(inputFields).some((inputField) => {
      return inputField.hasAttribute('aria-expanded')
    })
  }

  private isInputFieldFocused (inputField: HTMLInputElement): boolean {
    return inputField === document.activeElement
  }

  private resetSearchInputFields (): void {
    this.inputFields.forEach((searchInput): void => {
      searchInput.setAttribute('aria-expanded', 'false')
    })
  }

  private scrollToSelectedItem (autocompleteBox: HTMLElement, selectedItem: HTMLElement): void {
    if (!autocompleteBox || !selectedItem) return

    const itemTop = selectedItem.offsetTop
    const itemBottom = itemTop + selectedItem.offsetHeight
    const scrollTop = autocompleteBox.scrollTop
    const visibleTop = scrollTop
    const visibleBottom = scrollTop + autocompleteBox.clientHeight

    if (itemTop < visibleTop) {
      autocompleteBox.scrollTop = itemTop
    } else if (itemBottom > visibleBottom) {
      autocompleteBox.scrollTop = itemBottom - autocompleteBox.clientHeight
    }

    selectedItem.scrollIntoView({
      block: 'nearest',
      inline: 'nearest'
    })
  }
}

const searchAutocomplete = new SearchAutocomplete()
searchAutocomplete.init()
